import { useCallback, useEffect, useState } from "react";
import type { InfoPageId } from "./i18n/pages.ts";

/**
 * Every screen the app can be at. Kept as a discriminated union so navigation
 * is type-checked and the URL is derived from it rather than parsed ad hoc.
 */
export type Route =
  | { name: "home" }
  /**
   * `question` is the official question number (e.g. "1.1.01-001"). Addressing
   * by number rather than by position means a link keeps pointing at the same
   * question even when the pool around it changes, and each question gets its
   * own history entry.
   */
  | { name: "exam"; setId?: number; question?: string }
  | { name: "results" }
  | { name: "sheets" }
  | { name: "account" }
  | { name: "settings" }
  | { name: "info"; page: InfoPageId }
  | { name: "practice"; mode: PracticeRouteMode; question?: string };

export type PracticeRouteMode =
  | { kind: "new" }
  | { kind: "all" }
  | { kind: "search" }
  | { kind: "mistakes" }
  | { kind: "marked" }
  | { kind: "topic"; categoryId: number | null }
  | { kind: "sheet"; setId: number };

const INFO_PAGES: InfoPageId[] = ["help", "tips", "support", "imprint"];

/** Appends the question number when there is one. */
function withQuestion(base: string, question?: string): string {
  return question ? `${base}/${encodeURIComponent(question)}` : base;
}

export function routeToPath(route: Route): string {
  switch (route.name) {
    case "home": return "/";
    case "exam":
      return route.setId === undefined
        ? "/exam"
        : withQuestion(`/exam/${route.setId}`, route.question);
    case "results": return "/results";
    case "sheets": return "/sheets";
    case "account": return "/account";
    case "settings": return "/settings";
    case "info": return `/${route.page}`;
    case "practice": {
      const { mode, question } = route;
      if (mode.kind === "topic") {
        return mode.categoryId === null
          ? "/practice/topics"
          : withQuestion(`/practice/topic/${mode.categoryId}`, question);
      }
      if (mode.kind === "sheet") {
        return withQuestion(`/practice/sheet/${mode.setId}`, question);
      }
      return withQuestion(`/practice/${mode.kind}`, question);
    }
  }
}

/** Parses a trailing path segment as a question number. */
function asQuestion(segment: string | undefined): string | undefined {
  return segment ? decodeURIComponent(segment) : undefined;
}

export function parseRoute(pathname: string): Route {
  const parts = pathname.split("/").filter(Boolean);
  const [head, second, third, fourth] = parts;

  if (!head) return { name: "home" };

  if (head === "exam") {
    const id = Number(second);
    return {
      name: "exam",
      setId: Number.isFinite(id) && second ? id : undefined,
      question: asQuestion(third),
    };
  }
  if (head === "results") return { name: "results" };
  if (head === "sheets") return { name: "sheets" };
  if (head === "account") return { name: "account" };
  if (head === "settings") return { name: "settings" };
  if (INFO_PAGES.includes(head as InfoPageId)) {
    return { name: "info", page: head as InfoPageId };
  }

  if (head === "practice") {
    if (second === "topics") return { name: "practice", mode: { kind: "topic", categoryId: null } };
    if (second === "sheet") {
      const id = Number(third);
      if (Number.isFinite(id)) {
        return {
          name: "practice",
          mode: { kind: "sheet", setId: id },
          question: asQuestion(fourth),
        };
      }
      return { name: "practice", mode: { kind: "all" } };
    }
    if (second === "topic") {
      const id = Number(third);
      return {
        name: "practice",
        mode: { kind: "topic", categoryId: Number.isFinite(id) ? id : null },
        question: asQuestion(fourth),
      };
    }
    if (second === "new" || second === "all" || second === "search" ||
        second === "mistakes" || second === "marked") {
      return { name: "practice", mode: { kind: second }, question: asQuestion(third) };
    }
    return { name: "practice", mode: { kind: "all" } };
  }

  return { name: "home" };
}

/**
 * Current route plus a navigate function, backed by the History API so the
 * browser's back and forward gestures work as users expect.
 */
export function useRoute(): {
  route: Route;
  navigate: (to: Route, options?: { replace?: boolean }) => void;
} {
  const [route, setRoute] = useState<Route>(() => parseRoute(location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: Route, options?: { replace?: boolean }) => {
    const path = routeToPath(to);
    if (path !== location.pathname) {
      history[options?.replace ? "replaceState" : "pushState"]({}, "", path);
    }
    setRoute(to);
    window.scrollTo({ top: 0 });
  }, []);

  return { route, navigate };
}
